import Dropzone from 'dropzone';
import merge from 'lodash/merge';

import { hasFeature } from 'root/api/admin/features';
import { getCachedGatewayName } from 'root/api/helpers';
import { renderAddSavedCardModalLink } from 'root/apps/AddSavedCardModal/AddSavedCardModalLink';
import { renderRemoveLink } from 'root/apps/AddSavedCardModal/RemoveLink';
import { openSerializedRefundModal } from 'root/apps/SerializedRefund/SerializedRefundModal';
import { checkForActiveSerials } from 'root/apps/SerializedRefund/utils';
import { debugLsidEvent, randomLogId } from 'root/datadog/datadogLogs';
import { waitForElementToBeRemoved } from 'root/element';
import { hasCreditCardInfo } from 'root/regexValidation';

/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-escape */
/* eslint-disable no-plusplus */

const POST_TO_IFRAME_TIMEOUT_MS = 10000;
let uniqueId = 0;

export const SENSITIVE_INFO_PLACEHOLDER =
  "Don't enter sensitive information like login or credit card details";

export const SENSITIVE_INFO_WARNING = 'You entered sensitive information like credit card details.';
export const SENSITIVE_INFO_WARNING_ADD_LINK = 'Add the card';
export const SENSITIVE_INFO_WARNING_LINK_TEXT = 'in Saved Cards and/or';
export const SENSITIVE_INFO_WARNING_REMOVE_LINK = 'Delete it.';
export const SENSITIVE_INFO_WARNING_REMOVE_LINK_CUSTOMER_PAGE = 'delete it.';

class Utils implements MerchantosUtils {
  stringTrim = (value: string) => {
    return value.replace(new RegExp('^[\\s]+', 'g'), '').replace(new RegExp('[\\s]+$', 'g'), '');
  };

  postToIframe = async (id: string, url: string, data: { [key: string]: string }) => {
    const logId = randomLogId();
    debugLsidEvent('postToIframe', {
      id,
      type: data?.type || 'n/a',
      state: data?.state || 'n/a',
      logId,
    });
    await waitForElementToBeRemoved(`#${id}`, logId);

    const iframe = document.createElement('iframe');
    iframe.src = 'about:blank';
    iframe.id = id;
    iframe.name = id;
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'post';
    form.action = url;
    form.target = id;

    Object.entries(data).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();

    setTimeout(() => {
      iframe.remove();
    }, POST_TO_IFRAME_TIMEOUT_MS);
  };

  writeToClipboard = (selector: string) => {
    const element = document.querySelector<HTMLInputElement>(selector);

    if (!element) {
      return;
    }

    if (!navigator.clipboard) {
      element.focus();
      element.select();

      document.execCommand('copy');
    } else {
      navigator.clipboard.writeText(element.value);
    }
  };

  preventMultipleClicks = (target: HTMLAnchorElement) => {
    target.setAttribute('disabled', 'true');
    target.style.pointerEvents = 'none';
    target.style.color = 'black';
  };

  openUrl = (url: string, target: string | undefined) => {
    if ($('.netpromoter').is(':visible')) {
      window.hideNetPromoter();
    }

    let newUrl = url;
    if (!window.Ashes.isPhoenixRoute(url)) {
      const phoenixRoute = window.Ashes.createRoute(url);

      if (phoenixRoute && phoenixRoute.route) {
        newUrl = phoenixRoute.route;
      }
    }

    if (window.saveOnChanges().state() === 'pending') {
      return;
    }

    if (target && window.merchantos.register.confirmForUnsavedCreditPayments()) {
      window.open(newUrl, target);
    } else {
      window.location.href = newUrl;
    }

    // cancel any pending transactions
    window.merchantos.register.partial_payments.forceCancel();
  };

  startSerializedRefund = (
    arg:
      | string
      | {
          itemId: number;
          saleId: number;
          transactionLineId: number;
          itemDescription: string;
          qout: number;
          serials: Array<{
            serial: string;
            colorName: string;
            sizeName: string;
            serializedId: number;
            isSelected: boolean;
            isRefunded: boolean;
          }>;
        }
  ) => {
    let saleId;
    let transactionLineId;
    let itemId;
    let itemDescription;
    let serials;

    if (typeof arg === 'string') {
      ({ saleId, transactionLineId, itemId, itemDescription, serials } = JSON.parse(arg));
    } else if (typeof arg === 'object') {
      ({ saleId, transactionLineId, itemId, itemDescription, serials } = arg);
    }

    const activeSerials = checkForActiveSerials(serials);

    if (activeSerials.length > 1) {
      openSerializedRefundModal({ transactionLineId, saleId, itemId, itemDescription, serials });
    } else {
      window.merchantos.utils.openUrl(
        `register.php?form_name=refund_transaction_line&transaction_line_id=${transactionLineId}&item_id=${itemId}&transaction_id=${saleId}`
      );
    }
  };

  /**
   * Utility function to transform search param string into a key/value object
   *
   * @param {String} searchParams - A string containing the query parameters.
   * @return {Object} An object containing the key/value pairs of the search params.
   *
   * @example
   *
   * var searchParams = merchantos.utils.getQueryParams(window.location.search);
   */
  getQueryParams = (searchParams: string | boolean) => {
    let parsedParams = '';
    if (typeof searchParams !== 'string') {
      searchParams = '';
    }
    if (searchParams.indexOf('?') >= 0) {
      parsedParams = searchParams.split('?')[1];
    } else if (!/^\?/.test(searchParams) && searchParams.indexOf('=') >= 0) {
      // parse the params if it doesn't contain a leading '?' and still has the =
      parsedParams = searchParams;
    }

    if (!parsedParams) return {};
    return parsedParams
      .split('&')
      .map((param) => param.split('='))
      .reduce((params, param) => {
        const [key, value] = param;
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        // eslint-disable-next-line no-param-reassign
        if (!params[key]) params[key] = decodeURIComponent(value);
        return params;
      }, {});
  };

  /**
   * Utility function to transform search param object into a query string.
   *
   * @param {Object} params - An object containing key/value pairs of query params.
   * @return {String} The transformed query string.
   *
   * @example
   *
   * var queryParams = { hello: 'world', test: 123 };
   * var searchParams = merchantos.utils.getQueryString(queryParams);
   */
  getQueryString = (params: { [key: string]: any }) => {
    const paramKeys = Object.keys(params);
    return `?${paramKeys.reduce((result, key, index) => {
      let value;

      // if params[key] is an object then it's a multi select field - stringify to keep object intact
      if (typeof params[key] === 'object' && params[key] !== null) {
        value = JSON.stringify(params[key]);
      } else {
        value = params[key];
      }
      // eslint-disable-next-line no-param-reassign
      result += `${key}=${encodeURIComponent(value)}`;
      if (index !== paramKeys.length - 1) {
        // eslint-disable-next-line no-param-reassign
        result += '&';
      }

      return result;
    }, '')}`;
  };

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'form' implicitly has an 'any' type.
  loginRedirect = (form) => {
    if (form.action.length > 0) return true;

    const name = form.login_name.value;
    const password = form.login_password.value;

    const content_map = {} as ContentMap;
    content_map.method = 'POST';
    content_map.form_name = 'login_redirect';
    content_map.name = name;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'password' does not exist on type 'Conten... Remove this comment to see the full error message
    content_map.password = password;
    content_map.form_id = form.id;
    window.doServerCall(content_map, (result) =>
      window.merchantos.utils.loginRedirectReturn(result)
    );

    return false;
  };

  /**
   * Generate a URL to render this pannel
   *
   * First argument is a pannel object.
   * Second, optional, argument is a optional hash that will be hashed into the URL params
   *
   * */
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'pannel' implicitly has an 'any' type.
  generatePageUrl = (pannel, ...rest) => {
    const params = {} as { [key: string]: any };
    if (pannel.current_type === 'view') {
      params.name = pannel.view.name;
      params.form_name = 'view';
      params.id = pannel.view.record_id;
      if (pannel.view.tab) {
        params.tab = pannel.view.tab;
      }
      if (pannel.view.shipment_id) {
        params.shipment_id = pannel.view.shipment_id;
      }
    } else if (pannel.listing) {
      params.name = pannel.listing.name;
      params.form_name = 'listing';
      // Add on saved_search parameters
      // merge does a deep merge instead of shallow merge (extend)
      merge(params, pannel.listing.saved_search);
      // Supersede saved_search `__sort` and `__sort_dir` params
      // with the latest values from the listing.
      if (pannel.listing.sort) {
        params.__sort = pannel.listing.sort;
      }
      if (pannel.listing.sort_dir) {
        params.__sort_dir = pannel.listing.sort_dir;
      }
    }

    // Any optional params are mixed with the generated URL hash.
    if (window.isObjectValid(rest[0])) {
      const inject_params = rest[0];
      merge(params, inject_params);
    }

    return `/${window.merchantos.utils.getQueryString(params)}`;
  };

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'result' implicitly has an 'any' type.
  loginRedirectReturn = (result) => {
    const form = $<HTMLFormElement>(`#${result.form_id}`)[0];

    if (!result.url) {
      form.login_name.value = '';
      form.login_password.value = '';
      window.merchantos.focus.set(form.login_name);
      return;
    }

    form.action = result.url;
    form.trigger('submit');
  };
}

window.moneycomp = function moneycomp(a, comp, b, decimals) {
  // eslint-disable-next-line no-param-reassign
  if (!decimals) decimals = 2;
  const multiplier = 10 ** decimals;
  // eslint-disable-next-line no-param-reassign
  a = Math.round(a * multiplier);
  // eslint-disable-next-line no-param-reassign
  b = Math.round(b * multiplier);
  switch (comp) {
    case '>':
      return a > b;
    case '>=':
      return a >= b;
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '!=':
      return a !== b;
    case '==':
      return a === b;
    default: {
      return null;
    }
  }
};

window.sleep = function sleep(ms) {
  const dt = new Date();
  dt.setTime(dt.getTime() + ms);
  while (new Date().getTime() < dt.getTime());
};

/**
 * Switches view to a content pannel
 *
 * @param {String} type
 * @param {String} name
 * @param {String|Object} [init_search|id]
 * @param {String} [tab]
 * @returns {$.Deferred}
 */
window.navigateToContent = function navigateToContent(type, name, ...rest) {
  $('body,html').scrollTop(0);

  if ($('.netpromoter').is(':visible')) {
    window.hideNetPromoter();
  }

  // Optional attribute - position 3 (can be init_search or id)
  let search_params;
  const init_search = {};
  let id;
  // Optional attribute - position 4
  let tab;
  // Optional attribute - position 5
  let context;

  const loading = $('#loading');
  loading.fadeIn('fast');

  if (window.isObjectValid(rest[0])) {
    search_params = rest[0];
  } else if (window.isString(rest[0]) || window.isNumber(rest[0])) {
    id = rest[0];
  }

  // Used by register and workorder views
  if (window.isString(rest[1])) {
    tab = rest[1];
  }

  if (typeof rest[2] === 'object' && rest[2] !== null) {
    context = rest[2];
  }

  // build the page url for the new content
  let href = `/?form_name=${type}`;
  if (name) {
    href += `&name=${name}`;
  }
  if (id) {
    href += `&id=${id}`;
  }
  if (tab) {
    href += `&tab=${tab}`;
  }

  if (Array.isArray(search_params) && search_params.length) {
    $.each(search_params, (_, val) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      init_search[val[0]] = val[1];
      href += `&${val[0]}=${val[1]}`;
    });
  }

  // if we have loaded more than 20 new content items then do a hard page load for new content
  // this will help keep memory more tidy
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'dynamic_content_load_count' does not exi... Remove this comment to see the full error message
  if (!window.dynamic_content_load_count) {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'dynamic_content_load_count' does not exi... Remove this comment to see the full error message
    window.dynamic_content_load_count = 0;
  }

  // @ts-expect-error ts-migrate(2339) FIXME: Property 'dynamic_content_load_count' does not exi... Remove this comment to see the full error message
  window.dynamic_content_load_count++;
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'dynamic_content_load_count' does not exi... Remove this comment to see the full error message
  if (window.dynamic_content_load_count > 20) {
    window.merchantos.utils.openUrl(href);
  }

  // This is basically our static Router.
  // It's in a weird place
  // @TODO - Let's make this suck less.
  // open the new content
  if (type === 'listing') {
    return window.OpenListing('listing', name, init_search, tab);
  }
  if (type === 'view') {
    // @ts-expect-error FIXME: Type 'number' is not assignable to type 'string | undefined'
    return window.newView('view', name, id, tab, context);
  }
  return $.Deferred().resolve().promise();
};

window.saveBeforeClose = function saveBeforeClose() {
  // React pages will validate its dirty form fields
  // not using gui_def generic functions
  if (window.isReactPage) return;

  window.closing = true;
  if (window.hasUnsavedChanges()) {
    let prop;
    // eslint-disable-next-line
    for (prop in window.do_saves) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Save changes before exiting?')) return;
      break;
    }
    window.saveChanges();
  }
};

window.hasUnsavedChanges = function hasUnsavedChanges() {
  let hasChanges = false;

  if (window.isObjectValid(window.do_saves)) {
    hasChanges = Object.keys(window.do_saves).length > 0;
  }

  return hasChanges;
};

window.saveChanges = function saveChanges() {
  const savePromises = [];

  if (window.hasUnsavedChanges()) {
    // eslint-disable-next-line
    for (const prop in window.do_saves) {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
      savePromises.push(window.saveRecord(window.do_saves[prop]));
    }
  }

  return $.when(...savePromises);
};

window.isObjectValid = function isObjectValid(obj) {
  if (typeof obj === 'object' && obj !== null) return true;
  return false;
};

window.isString = function isString(str) {
  if (typeof str === 'string') return true;
  return false;
};

window.isNumber = function isNumber(number) {
  if (typeof number === 'number') return true;
  return false;
};

window.isURL = function isURL(url) {
  if (
    url.match(
      /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/
    )
  ) {
    return true;
  }
  return false;
};

window.isEmail = function isEmail(email) {
  if (email.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i)) {
    return true;
  }
  return false;
};

/**
 * checkForCreditCardInfo is used to validate presence of credit card info in an input field (text and textarea)
 * containerSelector: A container class/id which holds the input fields. The error message is rendered inside this container
 *
 * .error-tracker <div> element has <span> as children and each span holds the id of the input elements
 * that have CC info
 */
window.checkForCreditCardInfo = (element, options = {}) => {
  const $element = $(element);
  const containerSelector = options.containerSelector ?? $element.data('container-selector');
  const container = containerSelector ? $element.closest(containerSelector) : $element;
  const inputValue = options.value ?? $element.val()?.toString() ?? ' ';

  const errorMsgContainerClass = 'sensitive-info-error';
  const inputErrorTrackerContainerClass = 'error-tracker';

  const hasCreditCardNumber = hasCreditCardInfo(inputValue);

  const getErrorMessageDom = () =>
    containerSelector
      ? container.find(`.${errorMsgContainerClass}`)
      : container.siblings(`.${errorMsgContainerClass}`);
  const getErrorFieldListContainerDom = () =>
    containerSelector
      ? container.find(`.${inputErrorTrackerContainerClass}`)
      : container.siblings(`.${inputErrorTrackerContainerClass}`);

  const initDomErrorElements = async () => {
    uniqueId += 1;
    // FF guard
    const dataPanDetectionWarnBugfix = await hasFeature('DataPanDetectionWarnBugfix');
    // find out if the store is a multi-shop store
    const isMultiShopStore = window.merchantos.register.isMultiShop();
    /**
     * This function adds the error message and error tracker container in the DOM
     * if its not present already
     */
    const queryParams = new URLSearchParams(window.location.search);
    const isCustomerPage = queryParams.get('name') === 'customer.views.customer';
    let paramId = queryParams.get('id');
    if (paramId === 'undefined' || paramId === 'null') {
      paramId = null;
    }
    // also find out if the customer has opted in to save cards and has rights to add cards
    const customerOptIn = await new Promise<boolean>((resolve) => {
      window.lsCloud.Options.get('customer_use_card_on_file').done((setting) => {
        resolve(setting === '1');
      });
    });

    const hasAddCardRight = window.merchantos.session.hasRight('customers_add_card');

    // Only perform the gateway check if all the following conditions are met (This is to reduce the number of API calls)
    // The conditions are:
    // 1. The store is a multi-shop store
    // 2. The Bugfix for Data Pan Detection Warn feature is enabled
    // 3. If we are on the customer page and the customer ID is set (ie. Not a new customer)
    // 4. The User has the add card right and The customer has opted in to save cards

    // To start, this is the default case for single shop stores
    let showAddSavedCardLink =
      dataPanDetectionWarnBugfix && isCustomerPage && !!paramId && hasAddCardRight && customerOptIn;

    // If all of the conditions of showAddSavedCardLink is true and it's a multi-shop store
    // we need to check if the active gateway is lightspeed payments on the shop level
    // otherwise, we can assume the gateway is lightspeed payments through the rights checks on the account level
    if (showAddSavedCardLink && isMultiShopStore) {
      try {
        const gatewayNameQuery = await getCachedGatewayName();
        showAddSavedCardLink = gatewayNameQuery === 'lightspeed-payments';
      } catch (error) {
        // If we can't determine the gateway, don't show the link
        // It's okay to not show the link if we can't determine the gateway
        // as AddSavedCard link is not critical to the flow
        showAddSavedCardLink = false;
      }
    }

    if (!container.parent().find(`.${errorMsgContainerClass}`).length) {
      const errorMsg = `
        <div class="${errorMsgContainerClass}" data-test="error-message-container">
          <span class="icon-warning-sign"></span>
          <span class="text-content">
            ${SENSITIVE_INFO_WARNING}
            ${
              showAddSavedCardLink
                ? `<span id="add-saved-card-link-container-${uniqueId}"></span>
                ${SENSITIVE_INFO_WARNING_LINK_TEXT}
                <span id="remove-modal-link-container-${uniqueId}"></span>`
                : `<span id="remove-modal-link-container-${uniqueId}"></span>`
            }
          </span>
        </div>
      `;

      if (containerSelector) {
        container.append(errorMsg);
      } else {
        container.after(errorMsg);
      }

      // Render the React component for the Add Saved Card link
      // For now, we'll use a placeholder customerId which we will update later through the URL
      // or a customer ID from the DOM.
      // If the customer doesn't exist, we'll use 'new' as the customerId.
      const addLinkContainer = container
        .parent()
        .find(`#add-saved-card-link-container-${uniqueId}`)
        .first()[0];
      // the customerId could still be null if the customer doesn't exist yet (for a new entry) in which case we don't want this to render
      if (addLinkContainer && isCustomerPage && paramId) {
        renderAddSavedCardModalLink(addLinkContainer, paramId);
      }

      // Render the React component for the Remove Modal link
      const removeLinkContainer = container
        .parent()
        .find(`#remove-modal-link-container-${uniqueId}`)
        .first()[0];
      if (removeLinkContainer) {
        renderRemoveLink(removeLinkContainer, {
          container: container[0], // Convert jQuery object to HTMLElement
          showAddSavedCardLink,
        });
      }
    }

    if (!container.parent().find(`.${inputErrorTrackerContainerClass}`).length) {
      // Attach counter
      const fieldErrorTracker = `<div class="${inputErrorTrackerContainerClass}" style="display: none;"></div>`;
      if (containerSelector) {
        container.append(fieldErrorTracker);
      } else {
        container.after(fieldErrorTracker);
      }
    }
  };

  const updateErrorFieldTracker = () => {
    // Custom fields do not have id attribute. name attribute is the unique identifier for them
    const inputFieldIdentifier = $element.attr('id') || $element.attr('name');
    const errorHighlightCssClass = 'credit-card-info-error error';
    const fieldErrorTracker = `<span id=${inputFieldIdentifier}><span>`;

    if (hasCreditCardNumber) {
      $element.addClass(errorHighlightCssClass);
      if (!getErrorFieldListContainerDom().find(`#${inputFieldIdentifier}`).length) {
        getErrorFieldListContainerDom().append(fieldErrorTracker);
      }
    } else {
      $element.removeClass(errorHighlightCssClass);
      getErrorFieldListContainerDom().find(`#${inputFieldIdentifier}`).remove();
    }
  };

  // Call initDomErrorElements but don't await it since this function needs to remain synchronous
  // for backward compatibility with existing code
  initDomErrorElements()
    .then(() => {
      // After the async operation completes, update the error field tracker and visibility
      updateErrorFieldTracker();

      if (getErrorFieldListContainerDom().children().length) {
        getErrorMessageDom().addClass('visible');
      } else {
        getErrorMessageDom().removeClass('visible');
      }
    })
    .catch(() => {
      // Silently handle any errors in the async operation
    });

  // For immediate response, still update the error field tracker
  updateErrorFieldTracker();

  return !hasCreditCardNumber;
};

/**
 * Parses the element data-validation attribute and triggers each validation
 */
window.validateField = (element, options = {}) => {
  try {
    let isValid = true;
    const validations = element.dataset.validation?.split(',').map((v: string) => v.trim()) || [];
    if (validations.includes('checkForCreditCardInfo')) {
      if (!window.checkForCreditCardInfo(element, options)) {
        isValid = false;
      }
    }
    return isValid;
  } catch (e) {
    return false;
  }
};

/**
 * Triggers validation for all fields in the container
 */
window.validateFieldsInContainer = (element, options = {}) => {
  try {
    let isValid = true;
    element.querySelectorAll<HTMLElement>('[data-validation]').forEach((element) => {
      if (!window.validateField(element, options)) {
        isValid = false;
      }
    });
    return isValid;
  } catch (e) {
    return false;
  }
};

$('div#view').on('viewready', (event) => {
  hasFeature('DataPanDetectionWarn').then((hasFeature) => {
    if (!hasFeature) return;
    window.validateFieldsInContainer(event.target);
  });
});

window.objectPropertyCount = function objectPropertyCount(obj) {
  if (!window.isObjectValid(obj)) return 0;
  let count = 0;
  // eslint-disable-next-line
  for (const _ in obj) {
    // eslint-disable-next-line no-plusplus
    count++;
  }
  return count;
};

window.toJsonString = function toJsonString(arg) {
  return arg ? JSON.stringify(arg) : '';
};

// @ts-expect-error ts-migrate(2322) FIXME: Type '<T extends string | HTMLElement>(e: T) => T ... Remove this comment to see the full error message
window.xGetElementById = function xGetElementById(e) {
  if (typeof e === 'object' && e !== null) {
    return e;
  }
  return window.singleElementById(e);
};

window.toggleDisplay = function toggleDisplay(id) {
  const ele = window.xGetElementById(id);
  if (!ele) return;

  if (ele.style.display === 'none') ele.style.display = '';
  else ele.style.display = 'none';
};

window.toggleDisplayAndFocus = function toggleDisplayAndFocus(id) {
  window.toggleDisplay(id);
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
  window.setAutoFocus(id);
};

// @ts-expect-error ts-migrate(2322) FIXME: Type '<T extends string | HTMLElement>(e: T) => HT... Remove this comment to see the full error message
// eslint-disable-next-line consistent-return
window.singleElementById = function singleElementById(e) {
  if (typeof e === 'object' && e !== null) {
    return e;
  }
  if (e !== undefined && e !== '') {
    return $(`#${String(e).replace(/\./g, '\\.')}`)[0];
  }
};

window.custGetElementsByClassName = function custGetElementsByClassName(
  className,
  parentNode,
  nodeType
) {
  let selector = `.${className}`;
  if (typeof nodeType === 'string' && nodeType !== null) selector = nodeType + selector;

  if (typeof parentNode === 'object' && parentNode !== null) return $(parentNode).find(selector);
  return $(selector);
};

window.getObjectClass = function getObjectClass(obj) {
  if (obj && obj.constructor && obj.constructor.toString) {
    const arr = obj.constructor.toString().match(/function\s*(\w+)/);
    return arr && arr.length === 2 ? arr[1] : undefined;
  }
  return undefined;
};

// @ts-expect-error ts-migrate(2322) FIXME: Type '<T>(what: T) => {}' is not assignable to typ... Remove this comment to see the full error message
window.cloneObject = function cloneObject(what) {
  if (typeof what !== 'object') {
    return what;
  }
  let ret;
  // eslint-disable-next-line @typescript-eslint/ban-types
  if (window.getObjectClass(what as unknown as Record<string, string>) === 'Array') ret = [];
  else ret = {};
  // eslint-disable-next-line
  for (const i in what) {
    try {
      if (typeof what[i] === 'object') {
        // @ts-expect-error ts-migrate(2536) FIXME: Type 'Extract<keyof T, string>' cannot be used to ... Remove this comment to see the full error message
        // eslint-disable-next-line new-cap
        ret[i] = new window.cloneObject(what[i]);
        // @ts-expect-error ts-migrate(2536) FIXME: Type 'Extract<keyof T, string>' cannot be used to ... Remove this comment to see the full error message
      } else ret[i] = what[i];
    } catch (e) {
      // @ts-expect-error ts-migrate(2536) FIXME: Type 'Extract<keyof T, string>' cannot be used to ... Remove this comment to see the full error message
      ret[i] = what[i];
    }
  }
  return ret;
};

window.getRealPannelID = function getRealPannelID(pannel_id) {
  const pannel = window.xGetElementById(pannel_id);
  if (!pannel) return pannel_id;
  if (pannel.parent_id) return pannel.parent_id;
  return pannel.id;
};

// @ts-expect-error ts-migrate(2322) FIXME: Type '(pannel_id: PanelId) => false | HTMLElement ... Remove this comment to see the full error message
window.getRealPannel = function getRealPannel(pannel_id) {
  const pannel = window.xGetElementById(pannel_id);
  if (!pannel) return false;
  if (pannel.parent_id) return window.xGetElementById(pannel.parent_id);
  return pannel;
};

// eslint-disable-next-line no-extend-native
String.prototype.toCapitalize = function capititalizeString() {
  return this.toLowerCase().replace(/^.|\s\S/g, (a) => a.toUpperCase());
};

// Limit a string to maxSize and insert an ellipsis in the middle
// eslint-disable-next-line no-extend-native
String.prototype.shorten =
  String.prototype.shorten ||
  function shortenString(maxSize) {
    // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
    if (this.length > maxSize) {
      const charsFromFront = maxSize / 2;
      const charsFromEnd = maxSize - charsFromFront - (maxSize % 2 !== 0 ? 0 : 1); // account for ellipsis

      // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
      return `${this.substr(0, charsFromFront)}...${this.substr(
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.length - charsFromEnd,
        charsFromEnd
      )}`;
    }
    // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
    return this;
  };

/**
 * TODO: Eventually remove this when we're done with dropzone.
 * This is a hack that we used to have hardcoded when the library was in the /lib folder
 */
window.blockDropzoneFilesizeTranslation = function blockDropzoneFilesizeTranslation() {
  if (typeof Dropzone === 'undefined') return;
  // Add a `notranslate` class to the filesize method
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'filesize' does not exist on type 'Dropzo... Remove this comment to see the full error message
  // eslint-disable-next-line no-undef
  Dropzone.prototype.filesize = (size) => {
    let string;
    if (size >= 100000000000) {
      // operator-assignment no-param-reassign
      // eslint-disable-next-line
      size = size / 100000000000;
      string = 'TB';
    } else if (size >= 100000000) {
      // operator-assignment no-param-reassign
      // eslint-disable-next-line
      size = size / 100000000;
      string = 'GB';
    } else if (size >= 100000) {
      // operator-assignment no-param-reassign
      // eslint-disable-next-line
      size = size / 100000;
      string = 'MB';
    } else if (size >= 100) {
      // operator-assignment no-param-reassign
      // eslint-disable-next-line
      size = size / 100;
      string = 'KB';
    } else {
      // operator-assignment no-param-reassign
      // eslint-disable-next-line
      size = size * 10;
      string = 'b';
    }
    return `<strong notranslate>${Math.round(size) / 10}</strong> ${string}`;
  };
};

window.merchantos.utils = new Utils();
